import { Controller } from "@hotwired/stimulus"
import { cropMatrix3d } from "../lib/homography"

export default class extends Controller {
  static targets = ["container", "thumbnail", "mainImage", "boxOverlay", "tagPills", "questionName", "ratingContainer", "prevImageButton", "nextImageButton", "reviewBadge", "reviewBadgeDot", "reviewBadgeLabel", "cropToggle"]
  static values = {
    currentImageId: { type: Number, default: null },
    currentImageRotation: { type: Number, default: 0 },
    currentImageRating: { type: Number, default: null },
    initialImageId: { type: Number, default: null }
  }

  connect() {
    this.scale = 1
    this.originX = 0
    this.originY = 0
    this.rotation = 0 // Will be updated when first image loads
    this.lastX = 0
    this.lastY = 0
    this.isDragging = false
    this.boxZooming = false
    this.boxStartX = 0
    this.boxStartY = 0
    this.boxCurrentX = 0
    this.boxCurrentY = 0
    this.lastWheelTime = 0;
    this.wheelThrottleDelay = 50; // ms, adjust as needed for smoothness
    // Crop-preview (straighten) toggle state (ADR-0082 Phase C). When true the
    // straighten matrix3d sits on the INNER mainImage while the OUTER imageFrame
    // keeps the pan/zoom/rotate (the nest), so both compose — they do NOT fight.
    // The flag only tracks whether a straighten is engaged (so we clear the inner
    // transform on toggle-off / image switch) and hides the annotation overlays
    // while previewing. Always starts OFF and resets OFF on image switch.
    this.cropPreviewActive = false
    // Per-gallery review-status overrides, keyed by image id. Updated live on
    // review:updated events so navigating away and back reflects the new status
    // even though the modal_review_indicator dot (duplicated across dual-pane
    // previews) may be stale in this gallery's thumbnails. (#1808)
    this.reviewStatusOverrides = {}

    // Progressive full-res upgrade state (#4743 Phase 2). `upgraded` tracks
    // whether the CURRENTLY displayed image has already been swapped from
    // the medium variant to its raw original; reset on every nav
    // (showImage#_resetProgressiveUpgrade). `_pendingUpgradeRequest` holds
    // the in-flight preload `Image()` (used as an identity check so a stale
    // resolve can't land on the wrong image). `_pointerInsideGallery` tracks
    // hover for the pointerenter/pointerleave trigger below.
    this.upgraded = false
    this._pendingUpgradeRequest = null
    this._upgradeTimer = null
    this._pointerInsideGallery = false
    this._lastZoomGestureAt = 0
    this.hoverUpgradeDelay = 150 // ms -- pointerenter dwell before preloading; skips fly-bys
    this.navUpgradeDelay = 300 // ms -- debounce re-trigger after a nav while still hovering
    this.zoomRelevanceWindow = 1000 // ms a wheel/box-zoom gesture keeps the upgrade "relevant" for

    this.thumbnailTargets.forEach(thumbnail => {
      const img = thumbnail.querySelector("img")
      if (img) {
        img.addEventListener("click", this.showImage.bind(this))
      }
    })

    // Pan threshold (CSS px): the mousedown→move distance below which we treat the
    // gesture as a CLICK, not a pan. Until the pointer travels this far we neither
    // translate nor preventDefault, so a no-move press on a box still fires the
    // box's click→select. Mirrors the annotation draw controller's click threshold.
    this.panThreshold = 4
    this.panArmed = false // a left press is down, awaiting threshold to become a pan

    if (this.hasMainImageTarget) {
      this.mainImageTarget.style.transformOrigin = "center center";
      // The pan/zoom/rotate transform now lives on the OUTER imageFrame (the
      // nest), so its transition goes there; the inner img only carries the
      // straighten (no transition needed). Fall back to the img when the frame
      // wrapper is absent (defensive — older markup).
      this._panZoomTarget().style.transition = "transform 0.1s"
      // Pan + box-zoom mousedown listen on the CONTAINER, not the image — same
      // reasoning as the wheel listener below. The annotation overlay (saved-box
      // SVG rects, pointer-events:auto, z-30) is a SIBLING of the <img> sitting
      // ABOVE it, so a mousedown over a box targets the box rect and bubbles up
      // through the overlay's ancestors — never through the <img>. With the
      // listener on the image, a drag that STARTED over a box was eaten and the
      // pan never engaged (the left-drag-over-box bug, card #1689). The container
      // is the common ancestor of BOTH the image and every overlay layer, so a
      // mousedown anywhere over the image area (empty or over a box) bubbles up to
      // it and can start a pan. The gestures that must NOT pan (resize handle,
      // selected-box move-body, armed one-shot draw) are excluded by the bail
      // guard in startDrag, and a no-move press never pans (the threshold) so a
      // box click still selects.
      if (this.hasContainerTarget) {
        this.containerTarget.addEventListener("mousedown", this.startDrag)
        this.containerTarget.addEventListener("mousedown", this.startBoxZoom)
      } else {
        this.mainImageTarget.addEventListener("mousedown", this.startDrag)
        this.mainImageTarget.addEventListener("mousedown", this.startBoxZoom)
      }
      window.addEventListener("mousemove", this.onDrag)
      window.addEventListener("mouseup", this.endDrag)
      // Wheel-to-zoom listens on the CONTAINER, not the image. The annotation
      // overlay (saved-box SVG + box rects, pointer-events:auto) is a SIBLING of
      // the <img> sitting above it, so a wheel over a box targets the box rect and
      // bubbles up through the overlay's ancestors — never through the <img>. With
      // the listener on the image the box "ate" the scroll (the event simply never
      // reached onWheel). The container is the common ancestor of BOTH the image
      // and every overlay layer, so a wheel anywhere over the image area (empty or
      // over a box) bubbles up to it and zooms. Boxes keep pointer-events:auto for
      // hover/click/right-click; they don't listen for wheel, so it passes through
      // freely. (card #1689)
      if (this.hasContainerTarget) {
        this.containerTarget.addEventListener("wheel", this.onWheel, { passive: false })
        // Progressive full-res upgrade (#4743 Phase 2): a pointer resting
        // inside the gallery starts the hover-delay preload; leaving cancels
        // anything not yet fetched. See onGalleryPointerEnter/Leave below.
        this.containerTarget.addEventListener("pointerenter", this.onGalleryPointerEnter)
        this.containerTarget.addEventListener("pointerleave", this.onGalleryPointerLeave)
      }
      this.mainImageTarget.addEventListener("dblclick", this.resetTransform)
      // Suppress native HTML5 image drag (card #1689 regression). See preventDragStart.
      this.mainImageTarget.addEventListener("dragstart", this.preventDragStart)

      // Initialize with the first image's rotation if available
      this.initializeFirstImage()
    }

    // Track live review changes for the displayed image. When a review action
    // fires (from the modal context menu), review_actions_controller renders the
    // turbo stream — which replaces the acted image's thumbnail dot — then
    // dispatches a document-level "review:updated" event. The context menu lives
    // OUTSIDE the gallery's DOM subtree, so a document event is the reliable
    // cross-controller path: the chip has no per-image turbo target. By the time
    // the event fires the stream has already updated the thumbnail's
    // data-review-status, so re-running updateReviewDisplay reads the fresh value
    // for whichever image is currently shown. (ADR-0064, #1808)
    this.onReviewUpdated = this.onReviewUpdated.bind(this)
    document.addEventListener("review:updated", this.onReviewUpdated)

    // When an annotation in THIS pane changes (a crop drawn, a quad node dragged,
    // a crop deleted), the image-annotation controller dispatches a bubbling
    // "image-annotation:changed" from svgOverlay (a descendant of this element).
    // Re-derive the crop-toggle dataset off the live overlay so the toggle
    // straightens to the CURRENT crop shape (ADR-0082 Phase C). Listening on
    // this.element means only the editing pane's gallery responds — correct for
    // dual-pane. (#2086)
    this.onAnnotationChanged = this.onAnnotationChanged.bind(this)
    this.element.addEventListener("image-annotation:changed", this.onAnnotationChanged)
  }

  // Re-reflect the review status onto the chip after a review lands. The event
  // detail carries the acted image's id + resulting status. If the acted image
  // is the one currently shown in THIS gallery, set the chip straight from the
  // event status (authoritative — avoids reading the modal_review_indicator dot,
  // which is duplicated across dual-pane previews so only one copy gets replaced
  // by the stream). Also keep the gallery's own main-image data attribute in
  // sync so navigating away and back reflects the new status. (#1808)
  onReviewUpdated(event) {
    if (!this.hasMainImageTarget) return
    const detail = event.detail || {}
    if (detail.reviewableType !== "Image") return

    const actedId = parseInt(detail.reviewableId)
    const status = detail.status || ""

    // Cache the new status so any later navigation to this image reflects it.
    this.reviewStatusOverrides[actedId] = status

    // If the acted image is the one currently shown, refresh the chip now.
    const currentImg = this.mainImageTarget
    if (parseInt(currentImg.dataset.galleryImageId) === actedId) {
      currentImg.dataset.reviewStatus = status
      this.updateReviewDisplay(currentImg, status)
    }
  }

  initializeFirstImage() {
    // Check if we have an initial image ID specified
    if (this.initialImageIdValue && this.initialImageIdValue > 0) {
      // Try to find the specified initial image in thumbnails
      const targetThumbnail = this.thumbnailTargets.find(thumbnail => {
        const img = thumbnail.querySelector('img')
        return img && parseInt(img.dataset.galleryImageId) === this.initialImageIdValue
      })

      if (targetThumbnail) {
        const img = targetThumbnail.querySelector('img')
        // Show the specified image
        this.showImage({ currentTarget: img, preventDefault: () => { } })
        return
      }
    }

    // Initialize with main image data if available (fallback behavior)
    if (this.hasMainImageTarget) {
      const mainImg = this.mainImageTarget
      this.currentImageIdValue = parseInt(mainImg.dataset.galleryImageId) || null
      this.currentImageRotationValue = parseInt(mainImg.dataset.rotation) || 0
      // For rating, we need to handle both integer and string enum values
      const ratingData = mainImg.dataset.rating
      this.currentImageRatingValue = this.parseRatingValue(ratingData)
      this.rotation = this.currentImageRotationValue
      this.applyTransform()
      this.updateRatingDisplay()
      this.updateReviewDisplay(mainImg)
      this.updateQuestionDisplay(mainImg)
      // Sync crop geometry + toggle visibility for the initial image. The
      // mainImage already carries its own crop dataset (server-rendered), so
      // pass it as its own source.
      this._copyCropDataset(mainImg)
      // Highlight the initial thumbnail
      this.highlightCurrentThumbnail()
    } else {
      // Fallback to first thumbnail
      const firstThumbnail = this.thumbnailTargets[0]
      if (firstThumbnail) {
        const img = firstThumbnail.querySelector('img')
        if (img) {
          this.currentImageIdValue = parseInt(img.dataset.galleryImageId) || null
          this.currentImageRotationValue = parseInt(img.dataset.rotation) || 0
          const ratingData = img.dataset.rating
          this.currentImageRatingValue = this.parseRatingValue(ratingData)
          this.rotation = this.currentImageRotationValue
          this.applyTransform()
          this.updateRatingDisplay()
          this.updateReviewDisplay(img)
          this.updateQuestionDisplay(img)
          // The fallback first thumbnail's crop dataset must reach mainImage too.
          this._copyCropDataset(img)
          // Highlight the initial thumbnail
          this.highlightCurrentThumbnail()
        }
      }
    }
  }

  disconnect() {
    if (this.hasMainImageTarget) {
      if (this.hasContainerTarget) {
        this.containerTarget.removeEventListener("mousedown", this.startDrag)
        this.containerTarget.removeEventListener("mousedown", this.startBoxZoom)
        this.containerTarget.removeEventListener("wheel", this.onWheel)
        this.containerTarget.removeEventListener("pointerenter", this.onGalleryPointerEnter)
        this.containerTarget.removeEventListener("pointerleave", this.onGalleryPointerLeave)
      } else {
        this.mainImageTarget.removeEventListener("mousedown", this.startDrag)
        this.mainImageTarget.removeEventListener("mousedown", this.startBoxZoom)
      }
      window.removeEventListener("mousemove", this.onDrag)
      window.removeEventListener("mouseup", this.endDrag)
      this.mainImageTarget.removeEventListener("dblclick", this.resetTransform)
      this.mainImageTarget.removeEventListener("dragstart", this.preventDragStart)
    }
    this._clearUpgradeTimer()
    this._pendingUpgradeRequest = null

    if (this.onReviewUpdated) {
      document.removeEventListener("review:updated", this.onReviewUpdated)
    }
    if (this.onAnnotationChanged) {
      this.element.removeEventListener("image-annotation:changed", this.onAnnotationChanged)
    }
  }

  // Helper method to parse rating values from data attributes
  // Handles both integer and enumerize string values
  parseRatingValue(ratingData) {
    if (!ratingData || ratingData === '') return null

    // If it's already a number, convert it
    if (!isNaN(ratingData)) {
      return parseInt(ratingData)
    }

    // If it's an enumerize string, convert to integer
    switch (ratingData) {
      case 'hide': return -2
      case 'poor': return -1
      case 'good': return 1
      case 'winning': return 2
      case 'hero': return 3
      case 'unrated': return 0
      default: return 0
    }
  }

  showImage(event) {
    event.preventDefault()
    const img = event.currentTarget
    if (!img) return

    // Main display uses the medium ("review-size", Image#ai_review_size)
    // variant on EVERY nav, not the raw original -- matching the
    // server-rendered initial load (previews/components/_gallery.html.erb
    // already renders viewed_image.ai_review_size). The raw original is kept
    // on data-full-url for the open-in-new-tab link, the annotation menu's
    // open/copy actions (image_annotation_controller#imageUrlValue, set from
    // gallery:image-changed below), and the progressive full-res upgrade
    // further down this file. (#4743 Phase 2)
    const originalImageUrl = img.dataset.fullUrl || img.src
    const displayImageUrl = img.dataset.reviewUrl || originalImageUrl
    this.mainImageTarget.src = displayImageUrl
    this.mainImageTarget.alt = img.alt
    this.mainImageTarget.title = img.title

    // Update current image ID, rotation, and rating from data attributes
    this.currentImageIdValue = parseInt(img.dataset.galleryImageId) || null
    this.currentImageRotationValue = parseInt(img.dataset.rotation) || 0
    const ratingData = img.dataset.rating
    this.currentImageRatingValue = this.parseRatingValue(ratingData)

    // Write the now-displayed image's id/rotation/rating back onto the mainImage
    // DOM dataset. The mainImage element is shared across navigation (only its
    // src swaps), so its data attributes otherwise stay frozen on the INITIAL
    // image. Readers that resolve the displayed image from the DOM — the image
    // context menu (image_context_menu#beforeOpen reads dataset.galleryImageId),
    // onReviewUpdated, and reviewStatusForImage — would all act on the first
    // image forever. Syncing here keeps the attribute in lockstep with the src.
    this.mainImageTarget.dataset.galleryImageId = this.currentImageIdValue ?? ""
    this.mainImageTarget.dataset.rotation = img.dataset.rotation ?? ""
    this.mainImageTarget.dataset.rating = ratingData ?? ""
    // Carry the raw original onto the shared mainImage dataset too, so it
    // stays in lockstep with the displayed image for the same reason as the
    // three lines above -- the progressive-upgrade preload below always
    // reads the CURRENT image's original from here, not whichever image
    // happened to render first.
    this.mainImageTarget.dataset.fullUrl = originalImageUrl ?? ""

    // Crop geometry rides client-side navigation too (ADR-0082 Phase C): copy the
    // now-displayed image's crop_quad / crop_axis_aligned onto the shared mainImage
    // dataset so the toggle reads the right geometry, reset the preview OFF (never
    // carry a straighten across a navigation), and show/hide the toggle button for
    // whether THIS image has a crop.
    this._copyCropDataset(img)

    // Update tag pills
    const tagData = img.dataset.tags
    if (this.hasTagPillsTarget && tagData) {
      const tags = JSON.parse(tagData)
      this.tagPillsTarget.innerHTML = tags.map(tag => `<div class=\"pill-tag\" title=\"${tag}\">${tag}</div>`).join("")
    }

    // Update question name and title
    this.updateQuestionDisplay(img)

    // Update the icon link (top right) to the correct image_path
    const iconLink = this.containerTarget.querySelector("a")
    if (iconLink && img.dataset.imagePath) {
      iconLink.href = img.dataset.imagePath
    }

    // Reset transform but preserve database rotation
    this.scale = 1
    this.originX = 0
    this.originY = 0
    this.rotation = this.currentImageRotationValue // Initialize with persisted rotation
    this.applyTransform()

    // Update rating display
    this.updateRatingDisplay()

    // Update review-status badge for the now-displayed image
    this.updateReviewDisplay(img)

    // Highlight the current thumbnail
    this.highlightCurrentThumbnail()

    // Announce the image switch so the annotation overlay can follow it (card
    // #1689). The annotation pane is per-image, but the gallery swaps the
    // displayed image client-side with no server reload — so the boxes, list,
    // legend, and create-target must be re-fetched for the now-displayed image.
    // Dispatched on this controller's element (it bubbles) so the
    // image-annotation controller on the dual-pane wrapper hears it. Carries
    // the new image id + RAW ORIGINAL url (not the medium display variant) —
    // the listener's imageUrlValue feeds the context menu's "open in new tab"
    // / "copy image URL" actions, which must stay full-resolution.
    this.notifyImageChanged(originalImageUrl)

    // Progressive upgrade (#4743 Phase 2): every nav lands on the medium
    // variant, so drop any upgrade state/in-flight preload left over from the
    // PREVIOUS image and start clean for this one.
    this._resetProgressiveUpgrade()
    // If the pointer is already resting inside the gallery (e.g. holding
    // position while clicking next through a set), re-arm the preload on the
    // shorter nav debounce rather than making every single step of a
    // click-through wait out the full hover delay.
    if (this._pointerInsideGallery) this._scheduleUpgrade(this.navUpgradeDelay)
  }

  // Dispatch gallery:image-changed for the now-displayed image. Separate from
  // gallery:transform (which fires on every zoom/pan) so a listener can react to
  // an actual IMAGE switch without filtering transform noise.
  notifyImageChanged(imageUrl) {
    this.element.dispatchEvent(
      new CustomEvent("gallery:image-changed", {
        bubbles: true,
        detail: { imageId: this.currentImageIdValue, imageUrl: imageUrl }
      })
    )
  }

  // --- Progressive full-res upgrade (#4743 Phase 2) ---------------------------
  //
  // The main display always lands on the medium (ai_review_size) variant, on
  // both initial load and every nav. That's fast to fetch but not quite
  // pixel-perfect for zooming in close. So ahead of an expected zoom we
  // quietly preload the raw original and swap it in once it's fully decoded
  // -- by the time the reviewer actually zooms, the full-resolution image is
  // usually already sitting in the <img>.
  //
  // Three triggers, one entry point (_upgradeToFullRes):
  //   • pointerenter on the gallery container, after a short dwell (skips
  //     fly-bys -- a pointer that merely crosses the gallery never dwells
  //     long enough to fire it)
  //   • the same dwell, re-armed on a shorter debounce after a nav if the
  //     pointer is still inside (showImage)
  //   • immediately, as a fallback, on the first wheel tick / zoom button /
  //     box-zoom -- covers touch and keyboard interaction, where hover may
  //     never fire
  //
  // _upgradeToFullRes itself is idempotent (guarded by `upgraded` and
  // `_pendingUpgradeRequest`), so any combination of the three triggers
  // firing in quick succession only ever preloads once per image.

  // Pointer rests inside the gallery -- arm the hover-delay preload.
  onGalleryPointerEnter = () => {
    this._pointerInsideGallery = true
    this._scheduleUpgrade(this.hoverUpgradeDelay)
  }

  // Pointer leaves -- cancel anything not yet fetched. (An in-flight preload
  // that resolves after this still can't land: _isUpgradeRelevant will read
  // _pointerInsideGallery as false and the swap guard in _upgradeToFullRes's
  // resolution will bail, unless a zoom gesture independently keeps it
  // relevant.)
  onGalleryPointerLeave = () => {
    this._pointerInsideGallery = false
    this._clearUpgradeTimer()
    this._pendingUpgradeRequest = null
  }

  // Zoom-gesture fallback trigger: start the preload right away (no hover
  // delay) and stamp the "recently zooming" timestamp _isUpgradeRelevant
  // checks at swap time, so a wheel/box-zoom/zoom-button gesture justifies
  // the swap even with the pointer never having "entered" (touch, keyboard).
  _triggerZoomUpgrade() {
    this._lastZoomGestureAt = Date.now()
    this._clearUpgradeTimer()
    this._upgradeToFullRes()
  }

  // Debounced/delayed entry point shared by the hover and nav-while-hovering
  // triggers. Captures the image id at schedule time so a nav in the
  // meantime (which calls _resetProgressiveUpgrade, clearing this timer)
  // can't let a stale timer fire against the wrong image.
  _scheduleUpgrade(delay) {
    if (this.upgraded) return
    this._clearUpgradeTimer()
    const imageId = this.currentImageIdValue
    this._upgradeTimer = setTimeout(() => {
      this._upgradeTimer = null
      if (this.currentImageIdValue !== imageId) return
      this._upgradeToFullRes()
    }, delay)
  }

  _clearUpgradeTimer() {
    if (this._upgradeTimer) {
      clearTimeout(this._upgradeTimer)
      this._upgradeTimer = null
    }
  }

  // Called on every nav (showImage) to start the per-image upgrade state
  // clean: drop the previous image's timer/in-flight preload reference and
  // reset the upgraded flag, since the nav just landed on the medium variant
  // for a different image entirely.
  _resetProgressiveUpgrade() {
    this.upgraded = false
    this._pendingUpgradeRequest = null
    this._clearUpgradeTimer()
  }

  // True while the preload is still worth swapping in: the pointer is
  // resting inside the gallery, a box-zoom drag or held zoom-button is live,
  // or a wheel/box-zoom gesture fired recently enough to still count. Read
  // both when deciding whether to START a preload isn't required (the three
  // triggers already gate that) but is required right before the SWAP, so a
  // reviewer who has moved on by the time the preload resolves doesn't get a
  // surprise image change under their cursor.
  _isUpgradeRelevant() {
    if (this._pointerInsideGallery) return true
    if (this.boxZooming) return true
    if (this.zoomInterval) return true
    return Date.now() - this._lastZoomGestureAt < this.zoomRelevanceWindow
  }

  // Preload the raw original off-screen, then swap mainImage.src to it only
  // once fully decoded -- a seamless upgrade with no flicker. No pan/zoom/
  // rotate/annotation-overlay shift either: those live on the imageFrame
  // wrapper and sibling overlay layers (not this <img>), and object-contain
  // sizes off the container rather than the image's intrinsic dimensions, so
  // swapping `src` to a higher-resolution file of the same photo doesn't
  // change the rendered box.
  _upgradeToFullRes() {
    if (!this.hasMainImageTarget) return
    if (this.upgraded) return
    if (this._pendingUpgradeRequest) return

    const imageId = this.currentImageIdValue
    const originalUrl = this.mainImageTarget.dataset.fullUrl
    if (!originalUrl) return

    // Already showing the original (e.g. a legacy pre-Active-Storage image,
    // where ai_review_size falls back to the same url) -- nothing to do.
    if (this.mainImageTarget.src === originalUrl) {
      this.upgraded = true
      return
    }

    const preload = new Image()
    this._pendingUpgradeRequest = preload
    preload.src = originalUrl

    const ready = typeof preload.decode === "function"
      ? preload.decode()
      : new Promise((resolve, reject) => {
          preload.onload = resolve
          preload.onerror = reject
        })

    ready
      .then(() => {
        // Stale-load guard: only swap if this is still the pending request,
        // the image it was requested for is still the one displayed, and the
        // reviewer is still around to see it swap.
        if (this._pendingUpgradeRequest !== preload) return
        this._pendingUpgradeRequest = null
        if (this.currentImageIdValue !== imageId) return
        if (!this._isUpgradeRelevant()) return
        this.mainImageTarget.src = originalUrl
        this.upgraded = true
      })
      .catch(() => {
        if (this._pendingUpgradeRequest === preload) this._pendingUpgradeRequest = null
      })
  }

  // --- Crop-preview (straighten) toggle (ADR-0082 Phase C) --------------------

  // Copy the source image element's crop dataset onto the shared mainImage, reset
  // the preview OFF, and refresh the toggle button visibility for this image.
  // Called on every image switch (showImage + initializeFirstImage fallbacks) so
  // the geometry and button track the displayed image and a straighten never
  // carries across a navigation.
  _copyCropDataset(sourceImg) {
    if (!this.hasMainImageTarget || !sourceImg || !sourceImg.dataset) return
    const quad = sourceImg.dataset.cropQuad
    const axis = sourceImg.dataset.cropAxisAligned
    if (quad) {
      this.mainImageTarget.dataset.cropQuad = quad
      this.mainImageTarget.dataset.cropAxisAligned = axis ?? ""
    } else {
      delete this.mainImageTarget.dataset.cropQuad
      delete this.mainImageTarget.dataset.cropAxisAligned
    }
    // Always land OFF on a switch — clears any straighten + restores overlays.
    this._deactivateCropPreview()
    this._updateCropToggleVisibility()
  }

  // Does the currently-displayed image carry a crop? Reads off the mainImage
  // dataset (kept in lockstep with the displayed src by _copyCropDataset).
  _currentImageHasCrop() {
    return this.hasMainImageTarget && !!this.mainImageTarget.dataset.cropQuad
  }

  // Show the toggle only for an image that has a crop; clear its active styling.
  _updateCropToggleVisibility() {
    if (!this.hasCropToggleTarget) return
    const show = this._currentImageHasCrop()
    this.cropToggleTarget.classList.toggle("hidden", !show)
    this._setCropToggleActiveStyle(false)
  }

  // Toolbar button action: flip the straighten preview for the current image.
  toggleCropPreview() {
    if (!this._currentImageHasCrop()) return
    if (this.cropPreviewActive) {
      this._deactivateCropPreview()
    } else {
      this._activateCropPreview()
    }
  }

  // ON: straighten + frame the displayed image to its crop. The straighten goes
  // on the INNER img (mainImage) as a single matrix3d (transform-origin 0 0) that
  // maps the crop quad onto the upright content rect — destination IS the crop
  // framing. This is used for BOTH axis-aligned (the matrix reduces to a pure
  // scale+translate, no rotation/skew) and perspective quads, so one path. The
  // OUTER imageFrame keeps the gallery pan/zoom/rotate, so the two compose: the
  // image straightens, then zoom/pan/rotate ride on top (persisted rotation
  // honoured). The annotation overlays are hidden while previewing — annotating on
  // the straightened view stays out of scope (no H⁻¹ reconciliation).
  _activateCropPreview() {
    if (!this._applyCropTransform()) return
    this.cropPreviewActive = true
    this._setCropToggleActiveStyle(true)
    this._setOverlaysHidden(true)
  }

  // Apply the straighten matrix3d from the CURRENT crop dataset (cropQuad) onto
  // the inner img. Factored out so a live crop edit can re-apply in place (without
  // a toggle off/on) by re-reading the now-updated dataset. Returns true when a
  // transform was applied. ADR-0082 Phase C.
  _applyCropTransform() {
    if (!this.hasMainImageTarget) return false
    const img = this.mainImageTarget
    let quad
    try {
      quad = JSON.parse(img.dataset.cropQuad)
    } catch (e) {
      return false
    }
    if (!Array.isArray(quad) || quad.length !== 4) return false

    // The frame the img is laid out in (its own parent). Same size as the
    // container, but it is the img's actual content box for getImgRect.
    const frame = this.hasImageFrameTarget ? this.imageFrameTarget : this.containerTarget
    const matrix = cropMatrix3d(frame, img, quad)
    if (!matrix) return false
    img.style.transformOrigin = "0 0"
    img.style.transform = matrix
    return true
  }

  // OFF: clear the inner img's straighten and restore the raw annotatable view.
  // The OUTER frame's pan/zoom/rotate is untouched (independent transform), so
  // nothing to re-apply there; just show the overlays again.
  _deactivateCropPreview() {
    this.cropPreviewActive = false
    if (this.hasMainImageTarget) {
      this.mainImageTarget.style.transform = ""
      this.mainImageTarget.style.transformOrigin = "center center"
    }
    this._setCropToggleActiveStyle(false)
    this._setOverlaysHidden(false)
  }

  // An annotation in this pane changed (image-annotation:changed). Re-derive the
  // crop-toggle dataset off THIS gallery's live overlay so the toggle straightens
  // to the CURRENT crop shape — a quad-node edit, a newly drawn crop, or a crop
  // deletion all flow through here. Mirrors gallery_crop_data: the quad is the
  // explicit data-quad when present, else the bbox corners (TL→TR→BR→BL). (#2086)
  onAnnotationChanged() {
    if (!this.hasMainImageTarget) return

    const cropBox = this.element.querySelector(
      "[data-image-annotation-target='box'][data-label='crop']"
    )

    if (!cropBox) {
      // Crop deleted — drop the dataset, hide the button, end any live preview.
      delete this.mainImageTarget.dataset.cropQuad
      delete this.mainImageTarget.dataset.cropAxisAligned
      this._updateCropToggleVisibility()
      if (this.cropPreviewActive) this._deactivateCropPreview()
      return
    }

    const rawQuad = cropBox.dataset.quad
    let quadPoints = null
    if (rawQuad) {
      try {
        const parsed = JSON.parse(rawQuad)
        if (Array.isArray(parsed) && parsed.length === 4) quadPoints = parsed
      } catch (e) { /* fall back to bbox corners */ }
    }
    if (!quadPoints) {
      const bx = parseFloat(cropBox.dataset.bboxX)
      const by = parseFloat(cropBox.dataset.bboxY)
      const bw = parseFloat(cropBox.dataset.bboxWidth)
      const bh = parseFloat(cropBox.dataset.bboxHeight)
      quadPoints = [[bx, by], [bx + bw, by], [bx + bw, by + bh], [bx, by + bh]]
    }

    this.mainImageTarget.dataset.cropQuad = JSON.stringify(quadPoints)
    // Parity with gallery_crop_data: quad-if-present-else-bbox. The transform path
    // uses cropMatrix3d regardless, so this flag is dataset parity only.
    this.mainImageTarget.dataset.cropAxisAligned = rawQuad ? "false" : "true"
    this._updateCropToggleVisibility() // resets the button's active style to off
    // If the preview is engaged, re-apply the straighten in place so it updates to
    // the new shape without a toggle off/on, and re-assert the active button style
    // (visibility reset it).
    if (this.cropPreviewActive) {
      this._applyCropTransform()
      this._setCropToggleActiveStyle(true)
    }
  }

  // Sapphire fill + ring when the preview is engaged, plain white otherwise. The
  // button carries a STATIC hover:bg-white (for the inactive resting state); when
  // active we must turn that OFF and swap in hover:bg-sapphire/90, otherwise
  // hovering the engaged button repaints it white and washes the white icon out
  // (Fix F). hover:bg-sapphire/90 (a slightly lighter sapphire) is compiled —
  // it's used across the app, unlike the bare hover:bg-sapphire.
  _setCropToggleActiveStyle(active) {
    if (!this.hasCropToggleTarget) return
    const btn = this.cropToggleTarget
    btn.classList.toggle("bg-sapphire", active)
    btn.classList.toggle("text-white", active)
    btn.classList.toggle("ring-2", active)
    btn.classList.toggle("ring-sapphire", active)
    btn.classList.toggle("bg-white/80", !active)
    // Hover: keep the sapphire fill on the active button; restore white-hover when
    // inactive.
    btn.classList.toggle("hover:bg-white", !active)
    btn.classList.toggle("hover:bg-sapphire/90", active)
    const icon = btn.querySelector("svg")
    if (icon) {
      icon.classList.toggle("text-white", active)
      icon.classList.toggle("text-gray-700", !active)
    }
  }

  // Hide (or restore) the annotation overlay layers during a straighten preview.
  // The layers are siblings of the <img> inside the container, owned by the
  // image-annotation controller; we toggle visibility by DOM query so the gallery
  // controller stays decoupled from that controller's internals. boxOverlay is a
  // gallery target; the rest are queried by their image-annotation target attrs.
  _setOverlaysHidden(hidden) {
    const selectors = [
      "[data-image-annotation-target='svgOverlay']",
      "[data-image-annotation-target='polygonOverlay']",
      "[data-image-annotation-target='surface']",
      "[data-image-annotation-target='labels']",
      "[data-image-annotation-target='handles']"
    ]
    if (this.hasContainerTarget) {
      this.containerTarget.querySelectorAll(selectors.join(",")).forEach((el) => {
        el.style.visibility = hidden ? "hidden" : ""
      })
    }
    if (this.hasBoxOverlayTarget) {
      this.boxOverlayTarget.style.visibility = hidden ? "hidden" : ""
    }
  }

  // Kill the browser's native HTML5 image drag. The threshold-deferred
  // preventDefault in startDrag/onDrag (card #1689) means we do NOT preventDefault
  // on mousedown — so the native dragstart fires on the first movement and the
  // browser hijacks the gesture with a ghost drag-preview of the image instead of
  // panning. Suppressing dragstart specifically kills ONLY the native drag, leaving
  // the click-vs-pan threshold intact. Belt-and-braces alongside draggable="false"
  // on the <img>.
  preventDragStart = (event) => event.preventDefault()

  // A left press anywhere over the image area ARMS a potential pan. We do NOT
  // start translating yet, and we do NOT preventDefault — the actual pan only
  // engages once the pointer travels past panThreshold (in onDrag). That deferral
  // is what lets a no-move press on a box still reach the box's click→select: a
  // press-release with no travel never becomes a pan and never swallows the click.
  //
  // BAIL GUARD (card #1689): the press must NOT arm a pan when it originates on a
  // gesture the annotation overlay owns —
  //   • a resize handle or the selected box's move-body (the handles layer), or
  //   • the armed one-shot draw surface (pointer-events:auto only while armed).
  // Those are excluded here so dragging a handle resizes, dragging the box body
  // moves, and an armed draw rubber-bands — none of them hijacked into a pan. The
  // guard combines a DOM-target test (closest against the handles/surface layers)
  // with a flag the annotation controller exposes (isEditOrDrawActive), so a live
  // edit/draw drag bails even on the frames where its listeners moved to window.
  startDrag = (event) => {
    if (event.button !== 0 || event.altKey) return; // Only plain left mouse button
    if (this.shouldBailPan(event)) return
    // Arm — but defer translating + preventDefault until the threshold is crossed.
    this.panArmed = true
    this.isDragging = false
    this.panStartX = event.clientX
    this.panStartY = event.clientY
    this.lastX = event.clientX
    this.lastY = event.clientY
  }

  // True when a left press should NOT begin a pan because the annotation overlay
  // owns the gesture. No circular dependency: the gallery reads a DOM target and
  // calls a read-only query on the annotation controller; the annotation
  // controller never reaches into the gallery for this.
  shouldBailPan(event) {
    const target = event.target
    if (target && typeof target.closest === "function") {
      // The resize handles + move-body live in the handles layer; the armed draw
      // captures on the surface layer. A press on either belongs to that gesture.
      if (target.closest("[data-image-annotation-target='handles']")) return true
      if (target.closest("[data-image-annotation-target='surface']")) return true
    }
    // Belt-and-braces: a live edit/draw drag (listeners on window) also bails,
    // covering any frame where the press didn't land on the layer element itself.
    return this.annotationEditOrDrawActive()
  }

  // Ask the image-annotation controller (an ANCESTOR of this gallery element)
  // whether an edit (resize/move) or one-shot draw is currently active/armed.
  // Resolved via Stimulus; returns false if the controller isn't present.
  annotationEditOrDrawActive() {
    const el = this.element.closest("[data-controller~='image-annotation']")
    if (!el) return false
    const ctrl = this.application.getControllerForElementAndIdentifier(el, "image-annotation")
    return !!(ctrl && typeof ctrl.isEditOrDrawActive === "function" && ctrl.isEditOrDrawActive())
  }

  onDrag = (event) => {
    if (!this.panArmed) return
    const dx = event.clientX - this.lastX
    const dy = event.clientY - this.lastY

    // Until we cross the threshold this is still a candidate click: don't pan,
    // don't preventDefault, just watch the travel from the press point.
    if (!this.isDragging) {
      const travel = Math.hypot(event.clientX - this.panStartX, event.clientY - this.panStartY)
      if (travel < this.panThreshold) return
      // Threshold crossed → commit to a pan. Now is the moment to grab the cursor.
      this.isDragging = true
      this.mainImageTarget.style.cursor = "grabbing"
    }

    // A committed pan owns the gesture: suppress text selection / default drag.
    event.preventDefault()
    this.originX += dx
    this.originY += dy
    this.lastX = event.clientX
    this.lastY = event.clientY
    this.applyTransform()
  }

  endDrag = () => {
    this.panArmed = false
    this.isDragging = false
    this.mainImageTarget.style.cursor = "grab"
  }

  onWheel = (event) => {
    // Fallback progressive-upgrade trigger (#4743 Phase 2): the first wheel
    // tick on an image starts the full-res preload immediately, ahead of the
    // hover delay -- covers touch/keyboard zoom where pointerenter never (or
    // barely) fires. _upgradeToFullRes no-ops if already upgraded/in flight,
    // so repeated wheel ticks are harmless.
    this._triggerZoomUpgrade()

    const now = Date.now();
    if (now - this.lastWheelTime < this.wheelThrottleDelay) {
      event.preventDefault();
      return;
    }
    this.lastWheelTime = now;
    event.preventDefault()
    const prevScale = this.scale
    let newScale = this.scale * (event.deltaY < 0 ? 1.1 : 0.9)
    newScale = Math.max(0.2, Math.min(newScale, 20))

    if (newScale < prevScale) {
      // Zooming out: interpolate origin back toward center
      const t = 1 - newScale / prevScale
      this.originX = this.originX * (1 - t)
      this.originY = this.originY * (1 - t)
    }
    // Zooming in: do NOT adjust origin

    this.scale = newScale
    this.applyTransform()
  }

  zoomIn = () => {
    let newScale = this.scale * 1.25
    newScale = Math.min(newScale, 20)
    // Do NOT adjust origin when zooming in
    this.scale = newScale
    this.applyTransform()
  }

  zoomOut = () => {
    let newScale = this.scale * 0.8
    newScale = Math.max(newScale, 0.2)
    if (newScale < this.scale) {
      // Zooming out: interpolate origin back toward center
      const t = 1 - newScale / this.scale
      this.originX = this.originX * (1 - t)
      this.originY = this.originY * (1 - t)
    }
    this.scale = newScale
    this.applyTransform()
  }

  startZoomIn() {
    this._triggerZoomUpgrade() // fallback trigger (#4743 Phase 2) -- see onWheel
    this.stopZoom()
    this.zoomIn()
    this.zoomInterval = setInterval(() => this.zoomIn(), 60)
  }

  startZoomOut() {
    this._triggerZoomUpgrade() // fallback trigger (#4743 Phase 2) -- see onWheel
    this.stopZoom()
    this.zoomOut()
    this.zoomInterval = setInterval(() => this.zoomOut(), 60)
  }

  stopZoom() {
    if (this.zoomInterval) {
      clearInterval(this.zoomInterval)
      this.zoomInterval = null
    }
  }

  startBoxZoom = (event) => {
    if (!(event.button === 0 && event.altKey)) return; // Only Alt+Left Click
    this._triggerZoomUpgrade() // fallback trigger (#4743 Phase 2) -- see onWheel
    event.preventDefault();
    const rect = this.mainImageTarget.getBoundingClientRect();
    this.boxZooming = true;
    this.boxStartX = event.clientX - rect.left;
    this.boxStartY = event.clientY - rect.top;
    this.boxCurrentX = this.boxStartX;
    this.boxCurrentY = this.boxStartY;
    this.updateBoxOverlay();
    window.addEventListener("mousemove", this.onBoxZoomDrag);
    window.addEventListener("mouseup", this.endBoxZoom);
  }

  onBoxZoomDrag = (event) => {
    if (!this.boxZooming) return;
    const rect = this.mainImageTarget.getBoundingClientRect();
    this.boxCurrentX = Math.max(0, Math.min(rect.width, event.clientX - rect.left));
    this.boxCurrentY = Math.max(0, Math.min(rect.height, event.clientY - rect.top));
    this.updateBoxOverlay();
  }

  endBoxZoom = () => {
    if (!this.boxZooming) return;
    this.boxZooming = false;
    window.removeEventListener("mousemove", this.onBoxZoomDrag);
    window.removeEventListener("mouseup", this.endBoxZoom);
    this.zoomToBox();
    this.clearBoxOverlay();
  }

  updateBoxOverlay() {
    const overlay = this.boxOverlayTarget;
    const x = Math.min(this.boxStartX, this.boxCurrentX);
    const y = Math.min(this.boxStartY, this.boxCurrentY);
    const w = Math.abs(this.boxCurrentX - this.boxStartX);
    const h = Math.abs(this.boxCurrentY - this.boxStartY);
    overlay.innerHTML = `
      <div class="absolute border-2 border-blue-600 bg-blue-600/10 pointer-events-none"
           style="left:${x}px;top:${y}px;width:${w}px;height:${h}px"></div>
    `;
  }

  clearBoxOverlay() {
    this.boxOverlayTarget.innerHTML = "";
  }

  zoomToBox() {
    // this doesn't work but we'll leave it here for now
    // being able to see the box getting drawn could be cool for annotations
  }

  // Programmatically zoom + centre the gallery on a NORMALISED 0–1 image region
  // (card #1689 reverse cross-link). The annotation controller calls this with a
  // matched product box's normalised rect so clicking a ranging row frames that
  // box on the image.
  //
  // Maths mirrors applyTransform's model: the image fills the container and is
  // CSS-transformed `translate(origin) scale(scale) rotate(rotation)` about its
  // CENTRE (transform-origin: center center). The rendered image (object-contain)
  // sits at getRenderedImageRect() inside the container. So a normalised point
  // (nx,ny) is at untransformed container px P = rectLeft + nx*rectW, rectTop +
  // ny*rectH; after the transform (rotation aside) its screen position is
  // C + origin + scale*(P - C), where C is the container/image centre. To land the
  // region centre on C we set origin = -scale*(Pc - C). Scale is chosen so the box
  // fills ~targetFraction of the viewport on its tighter axis, clamped to the
  // gallery's own max zoom. We do NOT touch rotation — it stays at the persisted
  // image rotation, same as resetTransform. applyTransform() then emits
  // gallery:transform so the overlay/boxes/handles track the new view.
  zoomToRegion(rect, targetFraction = 0.65) {
    if (!rect || !this.hasContainerTarget || !this.hasMainImageTarget) return
    const imageRect = this.getRenderedImageRect()
    if (!imageRect || !imageRect.width || !imageRect.height) return

    const containerRect = this.containerTarget.getBoundingClientRect()
    const cw = containerRect.width
    const ch = containerRect.height
    if (!cw || !ch) return

    // Region size + centre in UNTRANSFORMED container px.
    const boxW = Math.max(rect.width, 0.001) * imageRect.width
    const boxH = Math.max(rect.height, 0.001) * imageRect.height
    const pcx = imageRect.left + (rect.x + rect.width / 2) * imageRect.width
    const pcy = imageRect.top + (rect.y + rect.height / 2) * imageRect.height

    // Scale so the box fills ~targetFraction of the viewport on its tighter axis,
    // clamped to the gallery's max zoom (20, matching onWheel/zoomIn). Never zoom
    // out below the current scale for a tiny box — keep at least 1×.
    const fit = Math.min((cw * targetFraction) / boxW, (ch * targetFraction) / boxH)
    const newScale = Math.max(1, Math.min(fit, 20))

    // Centre the region: origin = -scale * (regionCentre - containerCentre).
    const C = { x: cw / 2, y: ch / 2 }
    this.scale = newScale
    this.originX = -newScale * (pcx - C.x)
    this.originY = -newScale * (pcy - C.y)
    this.applyTransform()
  }

  getRenderedImageRect() {
    const img = this.mainImageTarget;
    const container = this.containerTarget;
    const imgAspect = img.naturalWidth / img.naturalHeight;
    const containerRect = container.getBoundingClientRect();
    const containerAspect = containerRect.width / containerRect.height;

    let width, height, left, top;
    if (imgAspect > containerAspect) {
      // Image fills width, letterbox top/bottom
      width = containerRect.width;
      height = width / imgAspect;
      left = 0;
      top = (containerRect.height - height) / 2;
    } else {
      // Image fills height, letterbox left/right
      height = containerRect.height;
      width = height * imgAspect;
      top = 0;
      left = (containerRect.width - width) / 2;
    }
    return { left, top, width, height };
  }

  // The element the gallery pan/zoom/rotate transform is written to: the OUTER
  // imageFrame wrapper (the nest), so it composes ON TOP of the inner img's
  // straighten. Falls back to the img if the frame wrapper is absent.
  _panZoomTarget() {
    return this.hasImageFrameTarget ? this.imageFrameTarget : this.mainImageTarget
  }

  applyTransform() {
    // Pan/zoom/rotate writes to the imageFrame (origin centre). The straighten,
    // when active, lives independently on the inner img (origin 0 0), so the two
    // compose — pan/zoom/rotate keep working during a crop preview and the
    // persisted rotation is honoured (rotate() is on the frame). ADR-0082 nest.
    this._panZoomTarget().style.transform =
      `translate(${this.originX}px, ${this.originY}px) scale(${this.scale}) rotate(${this.rotation}deg)`
    this.notifyTransform()
  }

  // The current image transform, for any sibling overlay (e.g. the annotation
  // overlay) that wants to track the image through zoom/pan/rotate. Mirrors the
  // values applyTransform writes, plus the CSS transform string itself so a
  // listener can apply the identical transform to a layer pinned to the same
  // rendered-image rect.
  transformState() {
    return {
      scale: this.scale,
      originX: this.originX,
      originY: this.originY,
      rotation: this.rotation,
      transform: `translate(${this.originX}px, ${this.originY}px) scale(${this.scale}) rotate(${this.rotation}deg)`
    }
  }

  // Announce a transform change so overlays anchored to the image can re-apply
  // the same transform. Dispatched on this controller's element so the
  // image-annotation controller (a sibling on the dual-pane wrapper) can listen.
  notifyTransform() {
    this.element.dispatchEvent(
      new CustomEvent("gallery:transform", { bubbles: true, detail: this.transformState() })
    )
  }

  rotateClockwise() {
    // Increment rotation and normalize to 0-360 range
    this.rotation = (this.rotation + 90) % 360
    // Update the stored rotation value
    this.currentImageRotationValue = this.rotation
    // Ensure smooth transition by avoiding 360->0 jump
    this.applyTransformWithoutTransition()
    // Persist rotation to database
    this.persistRotation()
  }

  rotateCounterClockwise() {
    // Decrement rotation and normalize to 0-360 range
    this.rotation = (this.rotation - 90 + 360) % 360
    // Update the stored rotation value
    this.currentImageRotationValue = this.rotation
    // Ensure smooth transition by avoiding 360->0 jump
    this.applyTransformWithoutTransition()
    // Persist rotation to database
    this.persistRotation()
  }

  applyTransformWithoutTransition() {
    // Temporarily disable transitions to prevent 360° animation. Operate on the
    // pan/zoom target (imageFrame) — that's where the rotate transform lives now.
    const el = this._panZoomTarget()
    const originalTransition = el.style.transition
    el.style.transition = 'none'
    this.applyTransform()
    // Force reflow to apply the change immediately
    el.offsetHeight
    // Re-enable transitions for other interactions
    el.style.transition = originalTransition
  }

  resetTransform() {
    this.scale = 1
    this.originX = 0
    this.originY = 0
    this.rotation = this.currentImageRotationValue // Reset to database rotation, not 0
    this.applyTransform()
  }

  async persistRotation() {
    if (!this.currentImageIdValue) {
      console.warn('No current image ID available for rotation persistence')
      return
    }

    try {
      const response = await fetch(`/images/${this.currentImageIdValue}/rotate`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        },
        body: JSON.stringify({
          rotation: this.rotation
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      // Update the thumbnail's data attribute to reflect the new rotation
      const currentThumbnail = this.thumbnailTargets.find(thumbnail => {
        const img = thumbnail.querySelector('img')
        return img && parseInt(img.dataset.galleryImageId) === this.currentImageIdValue
      })

      if (currentThumbnail) {
        const img = currentThumbnail.querySelector('img')
        if (img) {
          img.dataset.rotation = this.rotation.toString()
        }
      }

      console.log('Rotation persisted successfully:', data)
    } catch (error) {
      console.error('Failed to persist rotation:', error)
      // Optionally, show user feedback about the error
    }
  }

  // Helper method to update question display
  updateQuestionDisplay(img) {
    if (this.hasQuestionNameTarget) {
      const questionName = img.dataset.questionName || ''
      const questionTitle = img.dataset.questionTitle || ''
      if (questionName && questionTitle) {
        const fullText = `${questionName}: ${questionTitle}`
        this.questionNameTarget.innerHTML = `<p class="text-sm bg-sapphire text-white rounded-sm py-1 px-2 truncate w-64" title="${fullText}">${fullText}</p>`
      } else {
        this.questionNameTarget.innerHTML = ''
      }
    }
  }

  // Update the review-status badge to reflect the displayed image (ADR-0064).
  // Mirrors updateRatingDisplay: reads data-review-status off the displayed
  // image element and reflects it on the badge. Display-only chrome, so the
  // small status→classes map is inlined here (matched to the review_status_classes
  // helper colours). Hidden entirely when the image is unreviewed.
  updateReviewDisplay(img, explicitStatus = null) {
    if (!this.hasReviewBadgeTarget) return

    // Navigation path resolves the status from the image; the review-event path
    // passes the authoritative status straight through.
    const status = explicitStatus !== null ? explicitStatus : this.reviewStatusForImage(img)

    // status → coloured-pill classes, matched 1:1 to the badge partial and the
    // Response review buttons (responses/components/_review_actions). The chip
    // is the SAME primitive: coloured text + bg-50 + border-200, a plain -500
    // dot, rounded-md. Display-only chrome, so the small map is inlined here.
    const base = 'flex items-center gap-1 px-3 py-1 text-xs font-medium border rounded-md shadow-sm'
    const map = {
      approved: { chip: 'text-green-700 bg-green-50 border-green-200', dot: 'w-2 h-2 rounded-full bg-green-500', label: 'Approved' },
      rejected: { chip: 'text-red-700 bg-red-50 border-red-200', dot: 'w-2 h-2 rounded-full bg-red-500', label: 'Rejected' },
      deciding: { chip: 'text-amber-700 bg-amber-50 border-amber-200', dot: 'w-2 h-2 rounded-full bg-amber-500', label: 'Under Review' }
    }

    const config = map[status]
    if (!config) {
      this.reviewBadgeTarget.classList.add('hidden')
      return
    }

    // Set the full class string on the chip (base + colour variant, visible) so
    // background, border and text colour all switch together.
    this.reviewBadgeTarget.setAttribute('class', `${base} ${config.chip}`)
    if (this.hasReviewBadgeDotTarget) {
      this.reviewBadgeDotTarget.setAttribute('class', config.dot)
    }
    if (this.hasReviewBadgeLabelTarget) {
      this.reviewBadgeLabelTarget.textContent = config.label
    }
  }

  // Resolve the review status for the displayed image. Prefer the matching
  // thumbnail's indicator span (data-review-status) because the review turbo
  // stream replaces that span live — so the badge stays correct after a review
  // without a reload. Fall back to the passed image element's own data attribute
  // (covers the main-image-only initialisation path).
  reviewStatusForImage(img) {
    const imageId = img && img.dataset ? parseInt(img.dataset.galleryImageId) : this.currentImageIdValue
    // A live review override (set by onReviewUpdated) is authoritative — it
    // beats the possibly-stale duplicated thumbnail dot.
    if (this.reviewStatusOverrides && Object.prototype.hasOwnProperty.call(this.reviewStatusOverrides, imageId)) {
      return this.reviewStatusOverrides[imageId]
    }
    const thumbnail = this.thumbnailTargets.find(t => {
      const tImg = t.querySelector('img')
      return tImg && parseInt(tImg.dataset.galleryImageId) === imageId
    })
    const indicator = thumbnail ? thumbnail.querySelector('[data-review-status]') : null
    if (indicator) return indicator.dataset.reviewStatus || ''
    return img && img.dataset ? (img.dataset.reviewStatus || '') : ''
  }

  // Helper method to highlight the current thumbnail
  highlightCurrentThumbnail() {
    // Remove ring styling from all thumbnails and restore original borders
    this.thumbnailTargets.forEach(thumbnail => {
      thumbnail.classList.remove('ring-2', 'ring-sapphire', 'scale-95')
      // Restore original border if it was removed
      if (thumbnail.classList.contains('border-gray-100')) {
        thumbnail.classList.add('border')
      }
    })

    // Add ring styling to the current thumbnail
    if (this.currentImageIdValue) {
      const currentThumbnail = this.thumbnailTargets.find(thumbnail => {
        const img = thumbnail.querySelector('img')
        return img && parseInt(img.dataset.galleryImageId) === this.currentImageIdValue
      })

      if (currentThumbnail) {
        console.log('Highlighting thumbnail for image ID:', this.currentImageIdValue, currentThumbnail)
        // Remove border to avoid conflicts with ring and add scale to make ring visible
        currentThumbnail.classList.remove('border', 'border-gray-100')
        currentThumbnail.classList.add('ring-2', 'ring-sapphire', 'scale-95')
      } else {
        console.log('Could not find thumbnail for image ID:', this.currentImageIdValue)
        console.log('Available thumbnails:', this.thumbnailTargets.map(t => {
          const img = t.querySelector('img')
          return img ? parseInt(img.dataset.galleryImageId) : 'no img'
        }))
      }
    }

    // Update navigation button visibility
    this.updateImageNavButtons()
  }

  // Navigate to previous image in gallery (with wraparound)
  prevImage() {
    const currentIndex = this.getCurrentImageIndex()
    let targetIndex = currentIndex - 1

    // Wraparound: if we're at the first image, go to the last
    if (targetIndex < 0) {
      targetIndex = this.thumbnailTargets.length - 1
      console.log(`Gallery: wrapping around to last image - index ${targetIndex}`)
    } else {
      console.log(`Gallery: navigating to previous image - index ${targetIndex}`)
    }

    const targetThumbnail = this.thumbnailTargets[targetIndex]
    if (targetThumbnail) {
      const img = targetThumbnail.querySelector('img')
      if (img) {
        this.showImage({ currentTarget: img, preventDefault: () => { } })
      }
    }
  }

  // Navigate to next image in gallery (with wraparound)
  nextImage() {
    const currentIndex = this.getCurrentImageIndex()
    let targetIndex = currentIndex + 1

    // Wraparound: if we're at the last image, go to the first
    if (targetIndex >= this.thumbnailTargets.length) {
      targetIndex = 0
      console.log(`Gallery: wrapping around to first image - index ${targetIndex}`)
    } else {
      console.log(`Gallery: navigating to next image - index ${targetIndex}`)
    }

    const targetThumbnail = this.thumbnailTargets[targetIndex]
    if (targetThumbnail) {
      const img = targetThumbnail.querySelector('img')
      if (img) {
        this.showImage({ currentTarget: img, preventDefault: () => { } })
      }
    }
  }

  // Get current image index in thumbnail array
  getCurrentImageIndex() {
    return this.thumbnailTargets.findIndex(thumbnail => {
      const img = thumbnail.querySelector('img')
      return img && parseInt(img.dataset.galleryImageId) === this.currentImageIdValue
    })
  }

  // Update visibility of navigation buttons (with wraparound support)
  updateImageNavButtons() {
    const totalImages = this.thumbnailTargets.length
    const hasMultipleImages = totalImages > 1

    // With wraparound, show buttons as long as we have multiple images
    if (this.hasPrevImageButtonTarget) {
      if (hasMultipleImages) {
        this.prevImageButtonTarget.style.display = 'block'
        this.prevImageButtonTarget.title = 'Previous image (wraps around)'
      } else {
        this.prevImageButtonTarget.style.display = 'none'
      }
    }

    if (this.hasNextImageButtonTarget) {
      if (hasMultipleImages) {
        this.nextImageButtonTarget.style.display = 'block'
        this.nextImageButtonTarget.title = 'Next image (wraps around)'
      } else {
        this.nextImageButtonTarget.style.display = 'none'
      }
    }
  }

  // Rating methods
  setRating(event) {
    const button = event.currentTarget
    const newRating = button.dataset.rating

    // Convert empty string to null for unrated
    const ratingValue = newRating === '' ? 0 : parseInt(newRating)

    // Always add satisfying click animation (especially for confetti!)
    this.animateRatingClick(button, ratingValue)

    // Only update and persist if the rating is actually changing
    if (ratingValue !== this.currentImageRatingValue) {
      this.currentImageRatingValue = ratingValue
      this.updateRatingDisplay()
      this.persistRating()
    }
    // If rating is the same, we still get the animation but no server request
  }

  animateRatingClick(button, ratingValue) {
    // Remove any existing animation classes
    button.classList.remove('animate-bounce', 'scale-125', 'scale-150')

    // Add immediate scale effect
    button.classList.add('transition-transform', 'duration-150')

    if (ratingValue === 3) {
      // Hero (3): a distinctive "stamp / locked in" animation, no confetti.
      // The composite icon slams down and snaps into place.
      this.triggerHeroStamp(button)
    } else if (ratingValue === 2) {
      // Extra special animation for "Winning!" (2) rating only
      button.classList.add('scale-150')
      // Trigger confetti celebration!
      this.triggerConfetti(button)
      // Add a bounce effect
      setTimeout(() => {
        button.classList.remove('scale-150')
        button.classList.add('animate-bounce')
        setTimeout(() => {
          button.classList.remove('animate-bounce')
        }, 600) // Bounce duration
      }, 150)
    } else if (ratingValue !== null) {
      // Standard click animation for other ratings
      button.classList.add('scale-125')
      setTimeout(() => {
        button.classList.remove('scale-125')
      }, 150)
    } else {
      // Subtle animation for clearing rating
      button.classList.add('scale-110')
      setTimeout(() => {
        button.classList.remove('scale-110')
      }, 100)
    }
  }

  triggerHeroStamp(button) {
    // Animate the active composite icon (sapphire trophy + gold star) so it
    // reads as "stamped / BANG, locked in". The active glyph is made visible by
    // updateRatingDisplay; stamp the element that will be shown.
    const stampTarget = button.querySelector('[data-rating-active-icon]') || button

    // Make sure the composite is visible before stamping. updateRatingDisplay
    // also un-hides it, but stamping a hidden element would run the keyframes
    // off-screen. Defer to the next frame so the active state has applied.
    requestAnimationFrame(() => {
      // Reveal the composite as the mutually-exclusive pair (Bug 1, #1799): strip
      // `hidden`, add `inline-flex` — never leave both display utilities on at once.
      if (stampTarget.hasAttribute('data-rating-active-icon')) {
        this.showGlyph(stampTarget)
      } else {
        stampTarget.classList.remove('hidden')
      }

      // The press + shockwave ride the glyph; the kick rides the BUTTON (they
      // both animate transform, so they must live on separate elements — see
      // application.css). Re-trigger both by clearing → reflow → re-adding the
      // class so a repeated Hero click replays the slam cleanly (#1799 Bug 2).
      stampTarget.classList.remove('hero-stamp')
      button.classList.remove('hero-stamp-kick-host')
      // Force reflow so the animations restart on repeated Hero selections.
      void stampTarget.offsetWidth
      stampTarget.classList.add('hero-stamp')
      button.classList.add('hero-stamp-kick-host')

      // Clean both off on completion so the element rests in the persistent
      // active state (Bug 1). Wait for the LONGEST animation: the shockwave on
      // ::after ends last (320ms delay + 380ms = 700ms) and its animationend
      // bubbles to this element too, so we must filter by name — a bare
      // once:true would catch the 620ms press first and remove .hero-stamp,
      // truncating the ring's final fade.
      const onStampDone = (event) => {
        if (event.animationName !== 'hero-stamp-shockwave') return
        stampTarget.removeEventListener('animationend', onStampDone)
        stampTarget.classList.remove('hero-stamp')
        button.classList.remove('hero-stamp-kick-host')
      }
      stampTarget.addEventListener('animationend', onStampDone)
    })
  }

  triggerConfetti(triggerElement) {
    // Get the position of the trigger element (star button)
    const rect = triggerElement.getBoundingClientRect()
    const centerX = rect.left + rect.width * 0.4 // Slightly left of center
    const centerY = rect.top + rect.height * 0.2 // Much higher up on the star

    // Find the modal dialog element (parent container for confetti)
    const modal = triggerElement.closest('dialog') || triggerElement.closest('.modal') || document.body

    // Create confetti container
    const confettiContainer = document.createElement('div')
    confettiContainer.className = 'fixed inset-0 pointer-events-none'
    confettiContainer.style.cssText = 'overflow: hidden; z-index: 999999;'
    modal.appendChild(confettiContainer)

    // Colors for confetti pieces
    // --color-canary: #fcaf17;
    // --color-sapphire: #003d68;
    const colors = ['#fcaf17', '#FFD700', '#003d68', '#BCC6CC']

    // Create multiple confetti pieces
    for (let i = 0; i < 15; i++) {
      setTimeout(() => {
        this.createConfettiPiece(confettiContainer, centerX, centerY, colors)
      }, i * 50) // Stagger the creation slightly
    }

    // Clean up after animation completes
    setTimeout(() => {
      if (confettiContainer.parentNode) {
        confettiContainer.parentNode.removeChild(confettiContainer)
      }
    }, 2500)
  }

  createConfettiPiece(container, originX, originY, colors) {
    const piece = document.createElement('div')
    const color = colors[Math.floor(Math.random() * colors.length)]
    const size = Math.random() * 6 + 4 // 4-10px

    // Random shape - either square or circle
    const isCircle = Math.random() > 0.5

    piece.style.cssText = `
      position: absolute;
      width: ${size}px;
      height: ${size}px;
      background-color: ${color};
      border-radius: ${isCircle ? '50%' : '0'};
      left: ${originX}px;
      top: ${originY}px;
      transform-origin: center;
      pointer-events: none;
    `

    container.appendChild(piece)

    // Random trajectory - more upward and left like a sparkler
    const angle = (Math.random() - 0.9) * Math.PI * 0.2 - Math.PI * 0.5 // Very narrow cone, heavily left-biased
    const velocity = Math.random() * 250 + 150 // 150-400px distance
    const endX = originX + Math.cos(angle) * velocity
    const endY = originY + Math.sin(angle) * velocity - Math.random() * 150 - 80 // Strong upward drift

    // Random rotation
    const rotation = Math.random() * 720 + 360 // 1-2 full rotations

    // Animate the confetti piece
    piece.animate([
      {
        transform: 'translate(0, 0) rotate(0deg)',
        opacity: 1
      },
      {
        transform: `translate(${endX - originX}px, ${endY - originY}px) rotate(${rotation}deg)`,
        opacity: 0
      }
    ], {
      duration: Math.random() * 1000 + 1500, // 1.5-2.5 seconds
      easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)', // Smooth ease-out
      fill: 'forwards'
    })

    // Remove the piece when animation completes
    setTimeout(() => {
      if (piece.parentNode) {
        piece.parentNode.removeChild(piece)
      }
    }, 2500)
  }

  // Show / hide one of the Hero button's two glyph spans as a MUTUALLY EXCLUSIVE
  // pair (Bug 1, #1799). `hidden` (display:none) and `inline-flex` are BOTH display
  // utilities; stacked on one node the winner is decided by CSS source order and
  // `inline-flex` could beat `hidden`, leaving the element visible. So we never let
  // both sit on the same span: showing adds inline-flex AND strips hidden; hiding
  // adds hidden AND strips inline-flex. Exactly one glyph is visible at any time.
  showGlyph(el) {
    el.classList.remove('hidden')
    el.classList.add('inline-flex')
  }

  hideGlyph(el) {
    el.classList.remove('inline-flex')
    el.classList.add('hidden')
  }

  updateRatingDisplay() {
    if (!this.hasRatingContainerTarget) return

    const buttons = this.ratingContainerTarget.querySelectorAll('.rating-btn')
    const currentRating = this.currentImageRatingValue

    buttons.forEach(button => {
      const buttonRating = button.dataset.rating
      const buttonRatingValue = buttonRating === '' ? null : parseInt(buttonRating)

      // Reset button state (ring + background) for every button. bg-sapphire is
      // the Hero button's active-only circular fill (Hero's own tier colour);
      // strip it here so switching away from Hero removes the fill and each
      // render starts from the transparent ERB baseline. The Hero branch
      // re-applies bg-sapphire only when active. bg-blue-400 is left in the
      // removal list (harmless) to clear any legacy fill.
      button.classList.remove('ring-2', 'ring-red-300', 'ring-orange-300', 'ring-blue-400', 'ring-yellow-300', 'ring-sapphire', 'ring-slate-200', 'bg-white/30', 'bg-blue-400', 'bg-sapphire')

      // Hover reconciliation (#1799): the active Hero button fills with
      // bg-blue-400, so the shared white-overlay hover (hover:bg-white/20) would
      // wash the blue to near-white. The active Hero branch below swaps in
      // hover:bg-blue-500 (deepens the blue)
      // and drops the white-overlay hover. Reset both here so every non-active
      // render — including switching away from Hero — restores the shared
      // white-overlay hover and strips the Hero-only deeper-blue hover.
      button.classList.remove('hover:bg-blue-500')
      button.classList.add('hover:bg-white/20')

      // Check if this button represents the current rating
      const isActive = buttonRatingValue === currentRating

      // The Hero button (3) is a two-icon composite: toggle which representation
      // is visible instead of mutating a single svg's fill/stroke (which cannot
      // produce the trophy + star composite). This keeps the live active state
      // identical to the server-rendered state.
      const activeIcon = button.querySelector('[data-rating-active-icon]')
      const inactiveIcon = button.querySelector('[data-rating-inactive-icon]')

      if (activeIcon && inactiveIcon) {
        if (isActive) {
          // Active Hero ONLY: fill the circle with bg-sapphire (rgb 0,61,104) —
          // Hero's own tier colour, distinct from the "Good" thumbs-up blue. The
          // active glyph's trophy is gold (text-yellow-400, set in the ERB), which
          // pops on the sapphire and reads as an award (#1799). No star. No ring —
          // the filled sapphire disc is itself the selected affordance, and a
          // ring-sapphire on a sapphire fill would be invisible anyway. Inactive
          // Hero gets no fill — it stays transparent like its siblings, and the
          // reset line above strips bg-sapphire each pass so switching away from
          // Hero removes the fill.
          //
          // Hover: drop the shared hover:bg-white/20 (it would wash the sapphire)
          // for the active Hero pass. The reset lines above restore
          // hover:bg-white/20 for every non-active button.
          button.classList.remove('hover:bg-white/20')
          button.classList.add('bg-sapphire')
          this.showGlyph(activeIcon)
          this.hideGlyph(inactiveIcon)
        } else {
          this.showGlyph(inactiveIcon)
          this.hideGlyph(activeIcon)
        }
        return
      }

      // Single-glyph ratings: toggle fill/stroke on the one svg.
      const icon = button.querySelector('svg')
      if (!icon) return

      if (isActive) {
        // Active state: filled icon with matching ring and background
        button.classList.add('ring-2', 'bg-white/30')
        icon.style.fill = 'currentColor'
        icon.style.stroke = 'currentColor'

        // Set active colors with matching ring colors
        switch (buttonRating) {
          case '-2':
            icon.setAttribute('class', 'size-5 text-red-300 stroke-2')
            button.classList.add('ring-red-300')
            break
          case '-1':
            icon.setAttribute('class', 'size-5 text-orange-300 stroke-1')
            button.classList.add('ring-orange-300')
            break
          case '1':
            icon.setAttribute('class', 'size-5 text-blue-400 stroke-1')
            button.classList.add('ring-blue-400')
            break
          case '2':
            icon.setAttribute('class', 'size-5 text-yellow-300 stroke-2')
            button.classList.add('ring-yellow-300')
            break
          default:
            icon.setAttribute('class', 'size-4 text-gray-300 stroke-2')
            button.classList.add('ring-slate-200')
        }
      } else {
        // Inactive state: outline only with gray color and hover effects
        icon.style.fill = 'none'
        icon.style.stroke = 'currentColor'

        // Set inactive colors with hover effects
        switch (buttonRating) {
          case '-2':
            icon.setAttribute('class', 'size-5 text-gray-400 hover:text-red-400 stroke-2')
            break
          case '-1':
            icon.setAttribute('class', 'size-5 text-gray-400 hover:text-orange-400 stroke-1')
            break
          case '1':
            icon.setAttribute('class', 'size-5 text-gray-400 hover:text-blue-400 stroke-1')
            break
          case '2':
            icon.setAttribute('class', 'size-5 text-gray-400 hover:text-yellow-400 stroke-2')
            break
          default:
            icon.setAttribute('class', 'size-4 text-gray-400 hover:text-gray-300 stroke-2')
        }
      }
    })
  }

  async persistRating() {
    if (!this.currentImageIdValue) {
      console.warn('No current image ID available for rating persistence')
      return
    }

    try {
      const response = await fetch(`/images/${this.currentImageIdValue}/rate`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        },
        body: JSON.stringify({
          rating: this.currentImageRatingValue
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      // Update our current rating value with the response
      this.currentImageRatingValue = data.rating

      // Update the thumbnail's data attribute to reflect the new rating
      const currentThumbnail = this.thumbnailTargets.find(thumbnail => {
        const img = thumbnail.querySelector('img')
        return img && parseInt(img.dataset.galleryImageId) === this.currentImageIdValue
      })

      if (currentThumbnail) {
        const img = currentThumbnail.querySelector('img')
        if (img) {
          img.dataset.rating = data.rating !== null ? data.rating.toString() : ''
        }
      }

      // Also update main image data attribute
      if (this.hasMainImageTarget) {
        this.mainImageTarget.dataset.rating = data.rating !== null ? data.rating.toString() : ''
      }

      // Refresh the rating display to ensure consistency
      this.updateRatingDisplay()

      console.log('Rating persisted successfully:', data)
    } catch (error) {
      console.error('Failed to persist rating:', error)
      // Optionally, show user feedback about the error
    }
  }
}